import React, { useCallback, useContext, useEffect, useState } from "react";
import { Bell, CheckCheck, Mail, ShieldAlert, TrendingUp } from "lucide-react";
import { AuthContext } from "../AuthContext";
import { API_BASE_URL } from "../api";
import { Button, MetricCard, SectionHeader, SurfaceCard } from "../components/designSystem";

function getNotificationConfig(notification, displaySender) {
  if (notification.type === "message") {
    return {
      title: `Message from ${displaySender((notification.details && notification.details.sender) ? notification.details.sender : notification.user)}`,
      body: notification.details && notification.details.message ? notification.details.message : "You received a direct message.",
      icon: Mail,
      tone: "brand",
    };
  }

  if (notification.type === "trade") {
    return {
      title: "Trade activity",
      body: notification.details && notification.details.asset ? `${notification.details.asset} trade activity was recorded.` : "A trade update is available.",
      icon: TrendingUp,
      tone: "success",
    };
  }

  return {
    title: notification.type.replace(/-/g, " "),
    body: notification.details && notification.details.message ? notification.details.message : "A new account event is available.",
    icon: ShieldAlert,
    tone: "warning",
  };
}

export default function Notifications() {
  const { token } = useContext(AuthContext);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userCache, setUserCache] = useState({});
  const [error, setError] = useState("");

  const fetchNotificationsAndUsers = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE_URL}/api/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || "Failed to load notifications.");
      }
      setNotifications(Array.isArray(data) ? data : []);

      const senderIds = Array.from(new Set(
        (Array.isArray(data) ? data : [])
          .filter((n) => n.type === "message")
          .map((n) => (n.details && n.details.sender) ? n.details.sender : n.user)
      ));

      let cache = {};
      if (senderIds.length) {
        try {
          const resUsers = await fetch(`${API_BASE_URL}/api/find-user?ids=${senderIds.join(',')}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const users = await resUsers.json();
          (Array.isArray(users) ? users : []).forEach((u) => { cache[u._id] = u; });
        } catch (userErr) {
          setError(userErr.message || "Failed to resolve notification senders.");
        }
      }
      setUserCache(cache);
    } catch (err) {
      setError(err.message || "Failed to load notifications.");
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    if (!token) {
      return undefined;
    }

    fetchNotificationsAndUsers();
    const interval = setInterval(fetchNotificationsAndUsers, 30000);
    return () => clearInterval(interval);
  }, [fetchNotificationsAndUsers, token]);

  const markAsRead = async (id) => {
    try {
      await fetch(`${API_BASE_URL}/api/notifications/${id}/read`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications((current) => current.map((notification) => (
        notification._id === id ? { ...notification, read: true } : notification
      )));
    } catch (err) {
      setError(err.message || "Failed to update notification.");
    }
  };

  const displaySender = (senderId) => {
    const idStr = senderId ? String(senderId) : '';
    const u = userCache[idStr];
    if (u) {
      return u.name || u.email || u.username || idStr;
    }
    return "Unknown User";
  };

  const unreadCount = notifications.filter((notification) => !notification.read).length;

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <SurfaceCard className="overflow-hidden bg-[radial-gradient(circle_at_top_left,_rgba(14,165,233,0.14),_transparent_24%),linear-gradient(135deg,_#ffffff,_#f8fafc)] dark:bg-[radial-gradient(circle_at_top_left,_rgba(14,165,233,0.14),_transparent_24%),linear-gradient(135deg,_#111827,_#0f172a)]">
        <SectionHeader
          eyebrow="Notifications"
          title="Stay ahead of account and team activity"
          description="Notifications should surface the events that matter without feeling like noisy clutter."
        />
        <div className="mt-6 grid gap-4 md:grid-cols-3">
          <MetricCard label="Total" value={String(notifications.length)} helper="All visible notifications." />
          <MetricCard label="Unread" value={String(unreadCount)} helper="Items that still need attention." />
          <MetricCard label="Read" value={String(notifications.length - unreadCount)} helper="Notifications already reviewed." />
        </div>
      </SurfaceCard>

      {error ? (
        <SurfaceCard>
          <p className="text-sm font-medium text-rose-600 dark:text-rose-300">{error}</p>
        </SurfaceCard>
      ) : null}

      <SurfaceCard>
        <SectionHeader eyebrow="Feed" title="Recent activity" description="Messages, trade events, and account changes appear here in time order." />
        <div className="mt-6 space-y-4">
          {loading ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">Loading notifications...</p>
          ) : notifications.length ? (
            notifications.map((notification) => {
              const config = getNotificationConfig(notification, displaySender);
              const Icon = config.icon;

              return (
                <div key={notification._id} className={`rounded-3xl border p-5 ${notification.read ? "border-slate-200 bg-slate-50 dark:border-gray-700 dark:bg-gray-900" : "border-sky-200 bg-sky-50 dark:border-sky-500/20 dark:bg-sky-500/10"}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className="rounded-2xl bg-white/80 p-3 text-slate-900 dark:bg-gray-900/80 dark:text-white">
                        <Icon className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-950 dark:text-white">{config.title}</p>
                        <p className="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">{config.body}</p>
                        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{new Date(notification.createdAt).toLocaleString()}</p>
                      </div>
                    </div>
                    {!notification.read ? (
                      <Button type="button" variant="brand" className="shrink-0" onClick={() => markAsRead(notification._id)}>
                        <CheckCheck className="h-4 w-4" />
                        Mark read
                      </Button>
                    ) : (
                      <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700 dark:border-gray-700 dark:bg-gray-800 dark:text-slate-200">
                        <Bell className="h-3.5 w-3.5" />
                        Read
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="rounded-3xl border border-dashed border-slate-300 p-6 text-sm text-slate-500 dark:border-gray-700 dark:text-slate-400">
              No notifications yet. Account events and direct messages will appear here.
            </div>
          )}
        </div>
      </SurfaceCard>
    </div>
  );
}
